import React, { useMemo, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { Tag, ControlLink, Breakpoint, Stack, useBreakpoint } from '@moda/om';
import { compact, titleize } from '@moda/portal-stanchions';
import { PageFilterFragment, PageFilterFiltersFragment } from '../../generated/types';
import { usePLPParams } from '../../hooks/usePLPParams';
import { useCategoryTree } from '../../hooks/useCategoryTree';
import { useCountry } from '../CountrySelector';
import { AVAILABILITIES } from './PageFilterAvailability';

import './PageFilterClear.scss';

interface Props {
  attributes: PageFilterFiltersFragment['attributes'];
  designers: PageFilterFiltersFragment['designers'];
  sizes: PageFilterFiltersFragment['sizes'];
  categoryTree: PageFilterFragment['categoryTree'] | null;
}

export const PageFilterClear: React.FC<Props> = ({
  attributes,
  designers,
  sizes,
  categoryTree
}) => {
  const history = useHistory();
  const { country } = useCountry();
  const { params, removeFilter, clearFilter, clearAllFilters } = usePLPParams();

  const { selectedNodes, topLevelNode, getCategoryNodeHref, isNodeHidden } = useCategoryTree(
    categoryTree,
    (attributes?.length || 0) > 0
  );

  const categoryFilters = useMemo(
    () =>
      compact(
        [
          {
            type: 'vertical' as const,
            node: selectedNodes.vertical,
            parentNode: selectedNodes.root
          },
          {
            type: 'category' as const,
            node: selectedNodes.category,
            parentNode: selectedNodes.vertical
          },
          {
            type: 'subcategory' as const,
            node: selectedNodes.subcategory,
            parentNode: selectedNodes.category
          }
        ].map(({ type, node, parentNode }) => {
          if (!node?.slug || !node?.name || !parentNode || isNodeHidden(node)) return null;

          const href = getCategoryNodeHref(parentNode);

          if (!href) return null;

          return { key: type, value: node.slug, label: node.name, href };
        })
      ),
    [selectedNodes, getCategoryNodeHref, isNodeHidden]
  );

  const attributeFilters = useMemo(
    () =>
      params.attributes.map(value => {
        const attribute = attributes?.find(attribute => attribute?.slug === value);
        return {
          key: 'attributes' as const,
          value,
          label: titleize(attribute?.name || value)
        };
      }),
    [attributes, params.attributes]
  );

  const availabilityFilters = useMemo(
    () =>
      params.availability.map(value => {
        const availability = AVAILABILITIES.find(availability => availability.value === value);
        return {
          key: 'availability' as const,
          value,
          label: titleize(availability?.label || value)
        };
      }),
    [params.availability]
  );

  const colorFilters = useMemo(
    () =>
      params.colors.map(value => {
        return {
          key: 'colors' as const,
          value,
          label: titleize(value)
        };
      }),
    [params.colors]
  );

  const designerFilters = useMemo(
    () =>
      params.designers.map(value => {
        const designer = designers?.find(designer => designer?.slug === value);
        return {
          key: 'designers' as const,
          value,
          label: titleize(designer?.name || value)
        };
      }),
    [params.designers, designers]
  );

  const sizeFilters = useMemo(
    () =>
      params.sizes.map(value => {
        const size = sizes?.find(size => size?.filterValue === value);

        return {
          key: 'sizes' as const,
          value,
          label:
            size?.size ||
            (value.includes(':') ? value.split(':')[1].toUpperCase() : titleize(value))
        };
      }),
    [params.sizes, sizes]
  );

  const priceFilters = useMemo(
    () =>
      compact([
        params.pricemin != null
          ? {
              key: 'pricemin' as const,
              value: `min: ${params.pricemin}`,
              label: `${country.currencyCode} min: ${params.pricemin}`
            }
          : undefined,
        params.pricemax != null
          ? {
              key: 'pricemax' as const,
              value: `max: ${params.pricemax}`,
              label: `${country.currencyCode} max: ${params.pricemax}`
            }
          : undefined,
        params.sale ? { key: 'sale' as const, value: 'sale', label: 'On Sale' } : undefined
      ]),
    [country.currencyCode, params.pricemin, params.pricemax, params.sale]
  );

  const isMobile = useBreakpoint({ lt: 'sm' }).matches;

  const filters = useMemo(
    () => [
      ...(isMobile ? categoryFilters : []),
      ...availabilityFilters,
      ...attributeFilters,
      ...designerFilters,
      ...colorFilters,
      ...sizeFilters,
      ...priceFilters
    ],
    [
      categoryFilters,
      isMobile,
      availabilityFilters,
      attributeFilters,
      designerFilters,
      colorFilters,
      sizeFilters,
      priceFilters
    ]
  );

  const removeFilterOrCategory = useCallback(
    (filter: (typeof filters)[number]) => {
      if (filter.key === 'vertical' || filter.key === 'category' || filter.key === 'subcategory')
        return history.push(filter.href);

      if (filter.key === 'sale') return clearFilter(['sale', 'saletags']);

      return removeFilter(filter.key, filter.value);
    },
    [history, removeFilter, clearFilter]
  );

  const handleClearAll = useCallback(() => {
    const topLevelCategoryHref =
      topLevelNode && getCategoryNodeHref(topLevelNode, { removeFilters: true });

    return isMobile && topLevelCategoryHref
      ? history.push(topLevelCategoryHref)
      : clearAllFilters();
  }, [topLevelNode, getCategoryNodeHref, isMobile, history, clearAllFilters]);

  if (filters.length === 0) return null;

  const tags = filters.map(filter => (
    <Tag
      key={filter.value}
      className="PageFilterClear__tag"
      onClick={() => isMobile && removeFilterOrCategory(filter)}
      onRemove={() => removeFilterOrCategory(filter)}
      style={{ textTransform: 'capitalize' }}
    >
      {filter.label}
    </Tag>
  ));

  const clearAll = filters.length > 1 && (
    <ControlLink className="PageFilterClear__clear-all" onClick={handleClearAll}>
      Clear All
    </ControlLink>
  );

  return (
    <div className="PageFilterClear">
      <Breakpoint lt="md">
        <Stack space={3} alignItems="flex-start">
          <Stack className="PageFilterClear__filters" direction="horizontal" space={3}>
            {tags}
          </Stack>
          {clearAll}
        </Stack>
      </Breakpoint>

      <Breakpoint gt="md">
        {tags}
        {clearAll}
      </Breakpoint>
    </div>
  );
};
